import {
    initializeApp,
    FirebaseOptions,
    FirebaseApp,
} from 'firebase/app';
import {
    confirmPasswordReset,
    createUserWithEmailAndPassword,
    fetchSignInMethodsForEmail,
    getAuth,
    linkWithCredential,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    unlink,
    updatePassword,
    verifyPasswordResetCode,
    ActionCodeSettings,
    Auth,
    PhoneAuthProvider,
    RecaptchaVerifier,
    User,
} from 'firebase/auth';
import {
    addDoc,
    collection,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    CollectionReference,
    Firestore,
    Timestamp,
} from 'firebase/firestore';
import {
    getDownloadURL,
    getMetadata,
    getStorage,
    ref,
    uploadBytes,
    FirebaseStorage,
} from 'firebase/storage';
import {
    getFunctions,
    httpsCallable,
    Functions,
} from 'firebase/functions';
import {
    getAnalytics,
    logEvent,
    setUserId,
    setUserProperties,
    Analytics,
} from 'firebase/analytics';

import { v4 as uuid } from 'uuid';
import * as Sentry from '@sentry/browser';
import { RequestDocument } from '@core/documents/RequestDocument';
import ArtisanDocument from '@core/documents/ArtisanDocument';
import {
    IArtisanPublicData,
} from '@core/interfaces/IArtisan';
import CatchmentAreaId from '@core/constants/CatchmentArea';
import SubmissionSaleStatusId from '@core/constants/SubmissionSaleStatus';
import { SubmissionInternalQualificationId, SubmissionInternalContactQualificationId } from '@core/constants/SubmissionInternalSaleStatus';
import { SubmissionStatusId } from '@core/constants/SubmissionStatus';
import { JobId } from '@furnish-hx/constants/JobId';
import { ARBORESCENCE_VERSION } from '@core/constants/JobStructure';
import { ServiceId } from '@furnish-hx/constants/ServiceId';
import { ProjectId } from '@furnish-hx/constants/ProjectId';
import { FirebaseStorageBucketFolder } from '@core/constants/Firebase';
import SubmissionDocument from '@core/documents/SubmissionDocument';
import ArtisanProjectDocument from '@core/documents/ArtisanProjectDocument';
import {
    IRequestDeclineArtisanReasons, IRequestDeclineUserReasons, IRequestEstimate, IRequestQuote,
} from '@core/interfaces/IRequest';
import { RequestMessageSource, RequestStatusId } from '@core/constants/Request';
import { ArtisanProjectSourcePath } from '@core/constants/ArtisanProject';
import {
    ArtisanLabel, ArtisanMatchingPriority, ArtisanPlan, ArtisanStatus, PseudoPlan, PseudoPlanCommission,
} from '@core/constants/Artisan';
import { MaterialId } from '@core/constants/Material';
import FormId from '@furnish-hx/constants/FormId';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import getArtisanPublicDataUrl from '@core/utils/getArtisanPublicDataUrl';
import getSubmissionDataUrl from '@core/utils/getSubmissionDataUrl';
import getPublicSubmissionDataUrl from '@core/utils/getPublicSubmissionDataUrl';
import { ISubmissionDataResponseBody, ISubmissionData } from '@core/interfaces/ISubmissionData';
import { getArtisanProjectWithImageUrl } from '@core/utils/getArtisanProjectWithImageUrl';
import { getEstimateAttachmentsWithUrl } from '@core/utils/getEstimateAttachmentsWithUrl';
import { getQuoteAttachmentsWithUrl } from '@core/utils/getQuoteAttachmentsWithUrl';
import { IDocumentJsonSubmission } from '../../interfaces/IDocumentJsonSubmission';
import {
    moveLocationToProject, removePortfolioImagesURLs, transformDataToArtisanProject,
} from '../../transformers/ArtisanProjectTransformer';
import { IDocumentDataArtisan } from '../../interfaces/IDocumentDataArtisan';
import { IDocumentDataRequest } from '../../interfaces/IDocumentDataRequest';
import { IDocumentDataSubmission } from '../../interfaces/IDocumentDataSubmission';
import { IDocumentDataArtisanProject } from '../../interfaces/IDocumentDataArtisanProject';
import { transformDataToSubmission, transformJsonToSubmission } from '../../transformers/SubmissionTransformer';
import { transformDataToRequest } from '../../transformers/RequestTransformer';
import { transformDataToArtisan } from '../../transformers/ArtisanTransformer';
import { IArtisanData } from '../../view/pages/ArtisanOnboardingPage/types';
import {
    IUserKey,
    IUser,
    IUserCredentials,
    IUserRole,
} from '../../interfaces/User';
import {
    IFormKey,
    IForm,
    IFormFieldKey,
    IFormFieldDimension,
} from '../../interfaces/Form';
import {
    getFormWithOnlyFieldsValues,
} from '../../transformers/Form';
import {
    ActionId,
    ValueType,
    CtaId,
    FromId,
    DataId,
    UserProperty,
} from '../../constants/Analytics';

class FirebaseService {
    /** The Firebase service instantiation */
    constructor(config:FirebaseOptions) {
        this.app = initializeApp(config);
    }

    /** The firebase application */
    private readonly app: FirebaseApp;

    /** The firebase authentication */
    private auth: Auth | null = null;

    /** The firebase analytics */
    private analytics: Analytics | null = null;

    /** The firebase storage */
    private storage: FirebaseStorage | null = null;

    /** The firebase functions */
    private functions: Functions | null = null;

    /** The firestore database */
    private database: Firestore | null = null;

    /** The firestore database users collection reference */
    private usersCollectionReference: CollectionReference | null = null;

    /** The firestore database artisans collection reference */
    private artisansCollectionReference: CollectionReference | null = null;

    /** The firestore database artisan projects collection reference */
    private artisanProjectsCollectionReference: CollectionReference | null = null;

    /** The firestore database submissions collection reference */
    private submissionsCollectionReference: CollectionReference | null = null;

    /** The firestore database publicSubmissions collection reference */
    private publicSubmissionsCollectionReference: CollectionReference | null = null;

    /** The firestore database temporary submissions collection reference */
    private tmpSubmissionsCollectionReference: CollectionReference | null = null;

    /** The firestore database requests collection reference */
    private requestsCollectionReference: CollectionReference | null = null;

    private readonly getAuth = async () => {
        if (!this.auth) {
            this.auth = getAuth(this.app);
            const apiPromiseBuilder = (auth:Auth) => new Promise((resolve, reject) => {
                onAuthStateChanged(auth, (authUser: User | null) => {
                    if (authUser?.uid) {
                        setUserId(this.getAnalytics(), authUser.uid);
                        Sentry.addBreadcrumb({
                            category : 'auth',
                            message  : `Authenticated user ${authUser.uid}`,
                            level    : Sentry.Severity.Info,
                        });
                        Sentry.configureScope((scope) => {
                            scope.setUser({ id: authUser.uid });
                        });
                    }
                    resolve(authUser);
                }, reject);
            });

            await apiPromiseBuilder(this.auth);
        }

        return this.auth;
    }

    private readonly getAnalytics = () => {
        if (!this.analytics) { this.analytics = getAnalytics(this.app); }

        return this.analytics;
    }

    private readonly getStorage = () => {
        if (!this.storage) { this.storage = getStorage(this.app); }

        return this.storage;
    }

    private readonly getDatabase = () => {
        if (!this.database) { this.database = getFirestore(this.app); }

        return this.database;
    }

    private readonly getFirebaseFunctions = () => {
        if (!this.functions) { this.functions = getFunctions(this.app, 'europe-west1'); }

        return this.functions;
    }

    private readonly getUsersCollectionReference = () => {
        if (!this.usersCollectionReference) { this.usersCollectionReference = collection(this.getDatabase(), 'users'); }

        return this.usersCollectionReference;
    };

    private readonly getArtisansCollectionReference = () => {
        if (!this.artisansCollectionReference) { this.artisansCollectionReference = collection(this.getDatabase(), 'artisans'); }

        return this.artisansCollectionReference;
    };

    private readonly getSubmissionsCollectionReference = () => {
        if (!this.submissionsCollectionReference) { this.submissionsCollectionReference = collection(this.getDatabase(), 'submissions'); }

        return this.submissionsCollectionReference;
    };

    private readonly getArtisanProjectsCollectionReference = () => {
        if (!this.artisanProjectsCollectionReference) { this.artisanProjectsCollectionReference = collection(this.getDatabase(), 'artisan-projects'); }

        return this.artisanProjectsCollectionReference;
    };

    private readonly getPublicSubmissionsCollectionReference = () => {
        if (!this.publicSubmissionsCollectionReference) { this.publicSubmissionsCollectionReference = collection(this.getDatabase(), 'public-submissions'); }

        return this.publicSubmissionsCollectionReference;
    };

    private readonly getTmpSubmissionsCollectionReference = () => {
        if (!this.tmpSubmissionsCollectionReference) { this.tmpSubmissionsCollectionReference = collection(this.getDatabase(), 'tmp-submissions'); }

        return this.tmpSubmissionsCollectionReference;
    };

    private readonly getRequestsCollectionReference = () => {
        if (!this.requestsCollectionReference) { this.requestsCollectionReference = collection(this.getDatabase(), 'requests'); }

        return this.requestsCollectionReference;
    };

    /** The currently authenticated user from the authentication provider */
    private readonly getCurrentAuthUser = async () => (await this.getAuth()).currentUser;

    /** When new auth is created, user is not yet available in firestore, we need to emulate a local version during that time */
    private isUserEmulationNeeded: boolean = false;

    private currentUser?: IUser | null;

    /** Gets a specific user's reference in the firestore database
     * @param userId - The id of the user to get
     * @returns The user's reference in the firestore database */
    private readonly getUserReference = (userId: string) => doc(this.getUsersCollectionReference(), userId);

    /** Gets a specific user from the firestore database
     * @param userId - The id of the user to get
     * @returns {Promise<IUser>} - The user from the firestore database */
    private readonly getUser = async (userId: string) => ((await getDoc(this.getUserReference(userId))).data() as IUser);

    /** Fetches the database user from the passed auth user and returns the merged user
     * @param authUser - The user from the firebase auth
     * @return {Promise<IUser | null>} - The merged user */
    private readonly getMergedAuthAndDatabaseUser = async (authUser: User) => {
        try {
            return {
                id            : authUser.uid,
                emailVerified : authUser.emailVerified,
                ...(authUser.phoneNumber && { phone: authUser.phoneNumber }),
                ...(await this.getUser(authUser.uid)),
            };
        } catch (error) {
            Sentry.captureException(error);

            return null;
        }
    }

    /*------------*/
    /*    AUTH    */
    /*------------*/

    /** Signs a user in and returns the associated user from the firestore database if it exists
     * @param userCredentials - The credentials of the user with email and password
     * @returns {Promise<IUser | null>} - The user from the firestore database */
    public readonly signIn = async (userCredentials: IUserCredentials) => {
        const authUser = await signInWithEmailAndPassword(await this.getAuth(), userCredentials[IUserKey.Email], userCredentials[IUserKey.Password] as string);

        if (!authUser?.user?.uid) return null;
        logEvent(this.getAnalytics(), 'login', { method: 'password' });

        return this.getMergedAuthAndDatabaseUser(authUser.user);
    };

    /** Signs a user up, creates its user in the firestore database and returns it if
     * @param email - The user's email address
     * @param password - The user's chosen password
     * @returns {Promise<IUser|undefined>} - The user just created in the firestore database */
    public readonly signUp = async (email:string, password?:string) => {
        this.isUserEmulationNeeded = true;
        const authUser = await createUserWithEmailAndPassword(await this.getAuth(), email, password || uuid());

        if (!authUser?.user?.uid || !authUser?.user?.metadata?.creationTime) return undefined;
        this.currentUser = {
            [IUserKey.Id]    : authUser.user.uid,
            [IUserKey.Email] : email,
            [IUserKey.Role]  : IUserRole.User,
        };
        logEvent(this.getAnalytics(), 'sign_up', { method: 'password' });

        return this.currentUser;
    }

    /** Add PhoneNumber on current auth
     * @param phoneNumber - The user's phone number
     * @returns {Promise<(code: string) => Promise<boolean>>} - verify callback */
    public readonly attachPhoneNumber = async (phoneNumber:string) => {
        if (!this.currentUser) throw new Error('access denied');
        const currentAuth = await this.getAuth();

        if (!window.recaptchaVerifier) {
            await new Promise<void>((resolve, reject) => {
                window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', {
                    size     : 'invisible',
                    callback : (_response: unknown) => {
                        resolve();
                    },
                    'expired-callback': () => {
                        reject(new Error('reCAPTCHA timeout'));
                    },
                }, currentAuth);
                window.recaptchaVerifier.verify();
            });
        }

        const phoneAuthProvider = new PhoneAuthProvider(currentAuth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneNumber, window.recaptchaVerifier);

        return async (code:string) => {
            const authCredential = PhoneAuthProvider.credential(verificationId, code);
            const currentAuthUser = (await this.getAuth()).currentUser;
            if (currentAuthUser) {
                const userCredential = await linkWithCredential(currentAuthUser, authCredential);
                if (!userCredential?.user?.uid) throw new Error('linkWithCredential failed');

                return this.getMergedAuthAndDatabaseUser(userCredential.user);
            }

            throw new Error('currentAuthUser is missing');
        };
    }

    /** unlink PhoneNumber attached to current auth
     * @returns {Promise<void>} */
    public readonly unlinkPhoneNumber = async () => {
        const currentAuthUser = (await this.getAuth()).currentUser;
        if (currentAuthUser?.phoneNumber) {
            const user = await unlink(currentAuthUser, 'phone');

            return this.getMergedAuthAndDatabaseUser(user);
        }

        throw new Error('phoneNumber is missing');
    }

    /** Signs the currently authenticated user from the authentication provider out */
    public readonly signOut = async () => (await this.getAuth()).signOut()

    /** Checks if the email is not already used in the auth */
    public readonly isEmailAvailable = async (email: string) => (await fetchSignInMethodsForEmail(await this.getAuth(), email)).length === 0

    /** Sends a reset password email to the currently authenticated user
     * @param userEmail - The email of the user
     * @return {Promise<void>} */
    public readonly sendResetPasswordEmail = async (
        userEmail: string,
        actionCodeSettings?: ActionCodeSettings,
    ) => {
        await sendPasswordResetEmail(await this.getAuth(), userEmail, actionCodeSettings);
        this.trackAction(ActionId.REQUEST_RESET_PASSWORD);
    }

    /** Updates the password of the currently authenticated user
     * @param userPassword - The new user password
     * @return {Promise<void>} */
    public readonly updatePassword = async (userPassword: string) => {
        const currentUser = await this.getCurrentAuthUser();
        if (currentUser) await updatePassword(currentUser, userPassword);
    }

    /** Verifies if the user is in a valid reset password process, coming from its email
     * @param actionCode - The auth action code from the email link
     * @return {Promise<string>} - The user's email address */
    public readonly verifyPasswordResetCode = async (actionCode: string) => (
        verifyPasswordResetCode(await this.getAuth(), actionCode)
    )

    /** Updates the password of the currently authenticated user from the reset password process
     * @param actionCode - The auth action code from the email link
     * @param newPassword - The new user password
     * @return {Promise<void>} */
    public readonly submitPasswordReset = async (actionCode: string, newPassword: string) => {
        await confirmPasswordReset(await this.getAuth(), actionCode, newPassword);
        this.trackAction(ActionId.RESET_PASSWORD);
    }

    /** Adds a listener on the user authentication state changes and passes the new user state to the provided callback handler function
     * @param {callback(user: IUser | null) => void} - The callback function which will be passed the user
     * @return {Promise<firebase.Unsubscribe>} */
    public readonly onAuthUserListener = async (callback: (user: IUser | null) => void) => (await this.getAuth()).onAuthStateChanged(async (authUser: User | null) => {
        if (!authUser || !authUser?.uid || !authUser?.email) return callback(null);
        if (this.isUserEmulationNeeded) {
            this.currentUser = {
                id    : authUser.uid,
                email : authUser.email,
                role  : IUserRole.User,
            };
        } else {
            this.currentUser = await this.getMergedAuthAndDatabaseUser(authUser);
        }

        return callback(this.currentUser);
    });

    private extractUidAndEmailFromUser = (user:User | null) => {
        if (user?.email) {
            const { uid, email } = user;

            return { uid, email };
        }

        return null;
    };

    public readonly getCurrentUserIdAndEmail = () => new Promise<{uid:string, email:string} | null>((resolve, reject) => {
        this.getAuth().then((auth) => {
            if (auth?.currentUser) {
                resolve(this.extractUidAndEmailFromUser(auth.currentUser));
            } else {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                    unsubscribe();
                    resolve(this.extractUidAndEmailFromUser(user));
                }, reject);
            }
        });
    });

    public readonly isCurrentArtisan = async () => {
        const user = await this.getCurrentUserIdAndEmail();
        try {
            return user?.uid ? (await getDoc(doc(this.getArtisansCollectionReference(), user.uid))).exists : false;
        } catch {
            return false;
        }
    };

    /*-------------*/
    /*    ARTISAN  */
    /*-------------*/

    /** Gets the artisan from the firestore database and returns it
     * @param artisanId - The id of the artisan
     * @return {Promise<IArtisan | undefined>} - The artisan */
    public readonly getArtisan = async (artisanId: string) => {
        const artisan = await getDoc(doc(this.getArtisansCollectionReference(), artisanId));
        if (!artisan.exists) return undefined;

        return transformDataToArtisan(artisan.data() as IDocumentDataArtisan);
    }

    /** Gets the artisan public data
     * @param artisanSlug - The slug of the artisan
     * @return {Promise<IArtisanPublicData>} - The artisan public data */
    public readonly getArtisanPublicData = async (artisanSlug: string, withDefaultProject: boolean = false) => {
        const response = await fetch(getArtisanPublicDataUrl(window.location.host, artisanSlug, withDefaultProject));
        const result = await response.json();

        return result as IArtisanPublicData;
    }

    /** Creates a new artisan
     * @param artisanData
     * @returns {Promise<string>} - The artisan reference id */
    public readonly createArtisan = async (artisanData: Partial<IArtisanData>) => {
        // eslint-disable-next-line no-param-reassign
        delete artisanData.password;

        const result = await httpsCallable<Partial<IArtisanData>, {uid:string}>(this.getFirebaseFunctions(), 'createArtisan')(artisanData);
        if (!result.data?.uid) throw new Error(`createArtisan fails because of ${result.data}`);

        return result.data.uid;
    }

    /** Updates the appFormIds of an artisan
     * @param artisanId | the id of the artisan
     * @param appFormIds | The new list of app forms
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanAppFormIds = async (
        artisanId: string,
        appFormIds: FormId[],
    ) => httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({
        artisanId,
        appFormIds,
    })

    /** Updates the catchmentArea of an artisan
     * @param artisanId | the id of the artisan
     * @param catchmentArea | The new catchmentArea
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanCatchmentArea = (artisanId: string, catchmentArea: CatchmentAreaId[]) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, catchmentArea })
    );

    /** Updates the company data of an artisan
     * @param artisanId | the id of the artisan
     * @param companyData | The new company data
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanCompanyData = (artisanId: string, companyData: {
        companyAddress: string
        companyCity: string
        companyCountry: string
        companyZip: string
        firstName: string
        lastName: string
        linkFacebook?: string
        linkGoogleMyBusiness?: string
        linkInstagram?: string
        linkLinkedIn?: string
        linkWebsite?: string
        phone: string
        phoneSecondary?: string
    }) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, ...companyData })
    );

    /** Registers the last connection of an artisan to his dashboard
     * @param artisanId | the id of the artisan
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanConnection = (artisanId: string) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, lastConnection: true })
    );

    /** Updates the description of an artisan
     * @param artisanId | the id of the artisan
     * @param description | The new description
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanDescription = (artisanId: string, description: string) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, description })
    );

    /** Updates the description of an artisan
     * @param artisanId | the id of the artisan
     * @param googleMyBusinessId | The Google My Business Id
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanGoogleMyBusiness = (artisanId: string, googleMyBusinessId: string) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, googleMyBusinessId })
    );

    /** Updates artisan profile image
     * @param artisanId | the id of the artisan
     * @param labels | list of labels
     * @return {Promise<void>} - The id of the artisan */
    public readonly updateArtisanLabels = (
        artisanId: string,
        labels: ArtisanLabel[],
    ) => {
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, labels });
    }

    /** Updates the catchmentArea of an artisan
     * @param artisanId | the id of the artisan
     * @param materials | The new list of materials
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanMaterials = (artisanId: string, materials: MaterialId[]) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, materials })
    );

    /** Updates artisan profile image
     * @param artisanId | the id of the artisan
     * @param imageFile | the image File
     * @return {Promise<void>} - The id of the artisan */
    public readonly updateArtisanProfileImage = async (
        artisanId: string,
        imageBlob: Blob,
    ) => {
        const profileImage = artisanId;
        await uploadBytes(this.getArtisanProfileImageRef(profileImage), imageBlob);
        await httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, profileImage });
    }

    /** returns artisan profile image url
     * @param imageName | the image name
     * @return {Promise<string>} - image url */
    public readonly getArtisanProfileImageUrl = (imageName:string) => getDownloadURL(this.getArtisanProfileImageRef(imageName));

    private readonly getArtisanProfileImageRef = (imageName:string) => ref(this.getStorage(), `${FirebaseStorageBucketFolder.ArtisanProfileImages}/${imageName}`);

    /** Updates artisan profile logo
     * @param artisanId | the id of the artisan
     * @param logoFile | the logo File
     * @return {Promise<void>} - The id of the artisan */
    public readonly updateArtisanProfileLogo = async (
        artisanId: string,
        logoFile: Blob,
    ) => {
        const profileLogo = artisanId;
        await uploadBytes(this.getArtisanProfileLogoRef(profileLogo), logoFile);
        await httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, profileLogo });
    }

    /** returns artisan logo image url
     * @param logoName | the logo name
     * @return {Promise<string>} - logo image url */
    public readonly getArtisanProfileLogoUrl = (logoName:string) => getDownloadURL(this.getArtisanProfileLogoRef(logoName));

    private readonly getArtisanProfileLogoRef = (logoName:string) => ref(this.getStorage(), `${FirebaseStorageBucketFolder.ArtisanProfileLogos}/${logoName}`);

    /** Updates the catchmentArea of an artisan
     * @param artisanId | the id of the artisan
     * @param phoneSecondary | The new phoneSecondary
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanSecondaryPhone = (artisanId: string, phoneSecondary: string) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({ artisanId, phoneSecondary })
    );

    /** Updates the services of an artisan
     * @param artisanId | the id of the artisan
     * @param jobIds | The new list of jobIds
     * @param serviceIds | The new list of serviceIds
     * @param projectIds | The new list of projectIds
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanServices = (
        artisanId: string,
        jobIds: JobId[],
        serviceIds: ServiceId[],
        projectIds: ProjectId[],
    ) => httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({
        artisanId,
        jobIds,
        serviceIds,
        projectIds,
        arboVersion: ARBORESCENCE_VERSION,
    })

    /** Updates the subscription plan of an artisan
     * @param artisanId | the id of the artisan
     * @param plan | The new ArtisanPlan
     * @param subscriptionDate | The new subscriptionDate
     * @param subscriptionExpirationDate | The new subscriptionExpirationDate
     * @return {Promise<{ data:{ uid : string } }>} - The id of the artisan */
    public readonly updateArtisanSubscription = (artisanId: string, plan: PseudoPlan, subscriptionDate: Date | undefined, subscriptionExpirationDate: Date | undefined, matchingPriority:ArtisanMatchingPriority) => (
        httpsCallable(this.getFirebaseFunctions(), 'updateArtisan')({
            artisanId,
            plan                       : plan === PseudoPlanCommission ? ArtisanPlan.Freemium : plan,
            withCommission             : plan === PseudoPlanCommission,
            subscriptionDate           : subscriptionDate?.getTime(),
            subscriptionExpirationDate : subscriptionExpirationDate?.getTime(),
            matchingPriority,
        })
    );

    /** Updates the email of an artisan
     * @param artisanId | the id of the artisan
     * @param email | The new email
     * @return {Promise<void>} */
    public readonly updateArtisanEmail = (
        artisanId: string,
        email: string,
    ) => httpsCallable(this.getFirebaseFunctions(), 'updateArtisanEmail')({
        artisanId,
        email,
    })

    /*-------------------*/
    /*    SUBMISSIONS    */
    /*-------------------*/

    /** Gets the submission data from the related endpoint and returns it
     * @param submissionId - The id of the submission
     * @return {Promise<SubmissionDocument>} - The submission */
    public readonly getSubmissionData = async (submissionId: string, isPublic?: boolean) => {
        const response = isPublic
            ? await fetch(getPublicSubmissionDataUrl(window.location.host, submissionId))
            : await fetch(getSubmissionDataUrl(window.location.host, submissionId, false));
        const { id, data } = (await response.json());

        return new SubmissionDocument(id, transformJsonToSubmission(data as IDocumentJsonSubmission));
    }

    /** Gets the submission data from the related endpoint and returns it
     * @param submissionId - The id of the submission
     * @return {Promise<ISubmissionData>} - The submission and submissionAdditional data */
     public readonly getSubmissionAdditionalData = async (submissionId: string) => {
         const response = await fetch(getSubmissionDataUrl(window.location.host, submissionId, true));
         const { submission:submissionData, submissionAdditionalData } = (await response.json()) as ISubmissionDataResponseBody;

         return {
             submission: new SubmissionDocument(submissionId, submissionData),
             submissionAdditionalData,
         } as ISubmissionData;
     }

    /** Gets the submission form the firestore database and returns it
     * @param submissionId - The id of the submission
     * @return {Promise<SubmissionDocument>} - The submission */
    public readonly getSubmission = async (submissionId: string, isTemporary?: boolean) => {
        const docSnap = await getDoc(doc(isTemporary ? this.getTmpSubmissionsCollectionReference() : this.getSubmissionsCollectionReference(), submissionId));
        if (docSnap.exists()) return new SubmissionDocument(submissionId, transformDataToSubmission(docSnap.data() as IDocumentDataSubmission));
        throw new Error(`submission ${submissionId} not found`);
    }

    /** Gets the publicSubmission form the firestore database and returns it
     * @param publicSubmissionId - The id of the publicSubmission
     * @return {Promise<SubmissionDocument>} - The publicSubmission */
    public readonly getPublicSubmission = async (publicSubmissionId: string) => {
        const docSnap = await getDoc(doc(this.getPublicSubmissionsCollectionReference(), publicSubmissionId));
        if (docSnap.exists()) return new SubmissionDocument(publicSubmissionId, transformDataToSubmission(docSnap.data() as IDocumentDataSubmission));
        throw new Error(`public submission ${publicSubmissionId} not found`);
    }

    private readonly uploadFilesIfNecessary = async (form: Partial<IForm>, isTemporary: boolean) => {
        const partialForm = form;

        if (partialForm?.fields?.additionalData?.uploadFile) {
            const files = partialForm.fields.additionalData.uploadFile.value;

            if (files instanceof FileList) {
                const filesNames = [];

                for (let i = 0; i < files.length; i += 1) {
                    filesNames.push(this.getUniqueFileName(files.item(i) as File));
                }

                await Promise.all(filesNames.map(async (fileName, index) => (uploadBytes(ref(this.getStorage(),
                    `${isTemporary ? FirebaseStorageBucketFolder.TmpSubmissionsImages : FirebaseStorageBucketFolder.SubmissionsImages}/${fileName}`), files.item(index) as File)
                )));

                partialForm.fields.additionalData.uploadFile.value = filesNames;
            }
        }

        if (partialForm?.fields?.uploadFiles) {
            const files = partialForm?.fields?.uploadFiles?.value;

            if (files instanceof FileList) {
                const filesNames = [];

                for (let i = 0; i < files.length; i += 1) {
                    filesNames.push(this.getUniqueFileName(files.item(i) as File));
                }

                await Promise.all(filesNames.map(async (fileName, index) => (
                    uploadBytes(ref(this.getStorage(), `${isTemporary ? FirebaseStorageBucketFolder.TmpSubmissionsImages : FirebaseStorageBucketFolder.SubmissionsImages}/${fileName}`), files.item(index) as File)
                )));

                partialForm.fields.uploadFiles.value = filesNames;
            }
        }

        return form;
    }

    /** Creates a form submission in the firestore database and returns its reference
     * @param form - The submitted form
     * @return {Promise<DocumentReference>} - The reference of the newly created submission */
    public readonly createSubmission = async (form: Partial<IForm>, isTemporary?: boolean, artisanId?:string, isExternal?:boolean, artisanProjectSource?: string) => {
        const partialForm = form;
        const defaultBudget = partialForm.fields?.additionalData.budget?.defaultValue;

        if (!isTemporary && partialForm?.fields?.additionalData?.uploadFile?.value && Array.isArray(partialForm?.fields?.additionalData?.uploadFile?.value)) {
            const fileNames = partialForm.fields.additionalData.uploadFile.value;
            const downloadURLs = await Promise.all(fileNames.map(async (fileName) => getDownloadURL(ref(this.getStorage(), `${FirebaseStorageBucketFolder.TmpSubmissionsImages}/${fileName}`))));
            const blobs = await Promise.all(downloadURLs.map(async (downloadURL) => (await fetch(downloadURL)).blob()));
            await Promise.all(blobs.map(async (blob, index) => uploadBytes(ref(this.getStorage(), `${FirebaseStorageBucketFolder.SubmissionsImages}/${fileNames[index]}`), blob)));
        }

        if (!isTemporary && partialForm?.fields?.uploadFiles?.value && Array.isArray(partialForm?.fields?.uploadFiles?.value)) {
            const fileNames = partialForm.fields.uploadFiles.value;
            const downloadURLs = await Promise.all(fileNames.map(async (fileName) => getDownloadURL(ref(this.getStorage(), `${FirebaseStorageBucketFolder.TmpSubmissionsImages}/${fileName}`))));
            const blobs = await Promise.all(downloadURLs.map(async (downloadURL) => (await fetch(downloadURL)).blob()));
            await Promise.all(blobs.map(async (blob, index) => uploadBytes(ref(this.getStorage(), `${FirebaseStorageBucketFolder.SubmissionsImages}/${fileNames[index]}`), blob)));
        }

        await this.uploadFilesIfNecessary(form, !!isTemporary);

        const submission = {
            ...getFormWithOnlyFieldsValues(partialForm),
            [IFormKey.CreationDate]   : serverTimestamp(),
            [IFormKey.LastUpdateDate] : serverTimestamp(),
            [IFormKey.Status]         : SubmissionStatusId.Draft,
            ...(!isTemporary && {
                [IFormKey.UserId]   : (await this.getCurrentAuthUser())?.uid || this.currentUser?.id,
                [IFormKey.UserRole] : this.currentUser ? this.currentUser[IUserKey.Role] : undefined,
            }),
            ...(artisanId && {
                targetArtisan: artisanId,
            }),
            ...(isExternal && {
                isExternal: true,
            }),
            ...(artisanProjectSource && {
                artisanProjectSource,
            }),
            ...(defaultBudget && {
                defaultBudget,
            }),
        };

        return addDoc(isTemporary ? this.getTmpSubmissionsCollectionReference() : this.getSubmissionsCollectionReference(), submission);
    };

    /** Gets a submission image storage URL from file name
     * @param fileName - The name of the file
     * @return {Promise<string>} - URL of the image */
    public readonly getSubmissionImageURL = (fileName: string, isTemporary?: boolean) => (
        getDownloadURL(ref(this.getStorage(), `${isTemporary ? FirebaseStorageBucketFolder.TmpSubmissionsImages : FirebaseStorageBucketFolder.SubmissionsImages}/${fileName}`))
    );

    /** Creates a new publicSubmission
     * @param submissionId - The submission id
     * @returns {Promise<string>} - The submission reference id */
    public readonly createPublicSubmission = async (submissionId: string) => {
        const result = await httpsCallable<{submissionId:string}, string>(this.getFirebaseFunctions(), 'createPublicSubmission')({
            submissionId,
        });
        if (!result.data) throw new Error(`publicSumission fails because of ${result.data}`);

        return result.data;
    }

    /** Update a Submission
     * @param submissionId - The submission id
     * @returns {Promise<void>} */
    public readonly updateStatusAndCommentSubmission = async (submissionId: string, lilmStatus: SubmissionSaleStatusId, lilmComment?: string) => {
        const result = await httpsCallable<unknown, string>(this.getFirebaseFunctions(), 'updateSubmission')({
            submissionId,
            lilmStatus,
            ...(lilmComment && { lilmComment }),
        });
        if (result.data !== 'OK') throw new Error(`updateStatusAndCommentSubmission fails because of ${result.data}`);
    }

    public readonly updateSubmissionFields = async (submissionId: string, form:IForm) => {
        (await this.uploadFilesIfNecessary(form, false));
        updateDoc(doc(this.getSubmissionsCollectionReference(), submissionId), { fields: getFormWithOnlyFieldsValues(form).fields });
    }

    /** trigger Matching Auto
     * @param submissionId - The submission id
     * @returns {Promise<void>}  */
    public readonly tryToMatch = async (submissionId: string) => {
        const result = await httpsCallable<{submissionId:string}, string>(this.getFirebaseFunctions(), 'tryToMatch')({
            submissionId,
        });
        if (result.data !== 'OK') throw new Error(`tryToMatch fails because of ${result.data}`);
    }

    /** Update lilmInternalComment && lilmInternalContactQualificationStatus && lilmInternalQualificationStatus in a Submission
     * @param submissionId - The submission id
     * @returns {Promise<void>} */
     public readonly updateInternalCommentAndStatusSubmission = async (submissionId: string, lilmInternalContactQualificationStatus?: SubmissionInternalContactQualificationId, lilmInternalQualificationStatus?: SubmissionInternalQualificationId, lilmInternalComment?: string): Promise<void> => {
         const result = await httpsCallable<unknown, string>(this.getFirebaseFunctions(), 'updateSubmission')({
             submissionId,
             ...(lilmInternalContactQualificationStatus && { lilmInternalContactQualificationStatus }),
             ...(lilmInternalQualificationStatus && { lilmInternalQualificationStatus }),
             ...(lilmInternalComment && { lilmInternalComment }),
         });
         if (result.data !== 'OK') throw new Error(`updateInternalCommentAndStatusSubmission fails because of ${result.data}`);
     }

     /*------------------------*/
     /*    ARTISAN PROJECTS    */
     /*------------------------*/

    private readonly getArtisanProjectImagePathRef = (id:string) => ref(this.getStorage(), `${FirebaseStorageBucketFolder.ArtisanProjectsImages}/${id}`);

    private readonly getArtisanProjectImageSourceRef = (id:string) => ref(this.getArtisanProjectImagePathRef(id), ArtisanProjectSourcePath);

    private readonly getArtisanProjectImageRef = (id:string, name:string) => ref(this.getArtisanProjectImagePathRef(id), name);

    /** Uploads an artisan project image
     * @param imageBlob | The image Blob
     * @return {Promise<{id: string, url: string}>} - The id of the artisan */
    public readonly uploadArtisanProjectImage = async (
        imageBlob: Blob,
    ) => {
        const id = uuid();
        const storageChildRef = this.getArtisanProjectImageSourceRef(id);

        await uploadBytes(storageChildRef, imageBlob);

        return { id, url: await getDownloadURL(storageChildRef) };
    }

    private getArtisanProjectImageUrl = async (id: string, slug?:string) => {
        try {
            const imageRef = slug
                ? this.getArtisanProjectImageRef(id, slug)
                : this.getArtisanProjectImageSourceRef(id);

            return await getDownloadURL(imageRef);
        } catch {
            return undefined;
        }
    }

    /** Creates a new artisan project for an artisan
     * @param artisanProject - The artisan project data
     * @param artisanId - The artisan id to attach the project to
     * @returns {Promise<string>} - The project reference id */
    public readonly createArtisanProject = async (artisanId: string, artisanProject: Partial<IForm>) => {
        if (artisanId === undefined) throw new Error('artisanId is undefined');
        if (artisanProject === undefined) throw new Error('artisanProject is undefined');

        const result = await httpsCallable<unknown, {uid:string}>(this.getFirebaseFunctions(), 'createArtisanProject')({
            artisanId,
            artisanProject: getFormWithOnlyFieldsValues(moveLocationToProject(removePortfolioImagesURLs(artisanProject))),
        });

        if (!result.data?.uid) throw new Error(`createArtisanProject fails because of ${result.data}`);

        return result.data.uid;
    }

    /** Update artisan project
     * @param artisanProjectId - The id of the artisan project
     * @param artisanProject - The new artisan project data
     * @return {Promise<void>} */
    public readonly updateArtisanProject = async (artisanProjectId: string, artisanProject: Partial<IForm>) => {
        if (artisanProjectId === undefined) throw new Error('artisanProjectId is undefined');
        if (artisanProject === undefined) throw new Error('artisanProject is undefined');

        const result = await httpsCallable<unknown, {uid:string}>(this.getFirebaseFunctions(), 'updateArtisanProject')({
            artisanProjectId,
            artisanProject: getFormWithOnlyFieldsValues(removePortfolioImagesURLs(artisanProject)),
        });

        if (!result.data?.uid) throw new Error(`updateArtisanProject fails because of ${result.data}`);

        return result.data.uid;
    };

    /** Gets the artisan project from the firestore database and returns it
     * @param artisanProjectId - The id of the artisan project
     * @return {Promise<ArtisanProjectDocument>} - The artisan project */
    public readonly getArtisanProject = async (artisanProjectId: string) => {
        const projectsSnapshot = await getDoc(doc(this.getArtisanProjectsCollectionReference(), artisanProjectId));
        const transformedProject = await getArtisanProjectWithImageUrl(this.getArtisanProjectImageUrl, transformDataToArtisanProject(projectsSnapshot.data() as IDocumentDataArtisanProject));

        return new ArtisanProjectDocument(projectsSnapshot.id, transformedProject);
    };

    /** Lists the artisan projects of a particular artisan in the firestore database
     * @param artisanId | the id of the artisan
     * @param onlyPublished | should list only the published artisan projects ?
     * @return {Promise<ArtisanProjectDocument[]>} - artisan projects */
    public readonly listArtisanProjects = async (artisanId: string, onlyPublished?: boolean) => {
        const isPublished = onlyPublished
            ? where('isPublished', '==', true)
            : where('isDeleted', '==', false);

        const artisanProjectsSnapshots = await getDocs(query(this.getArtisanProjectsCollectionReference(), where('artisanId', '==', artisanId), isPublished));

        const artisanProjects = await Promise.all(artisanProjectsSnapshots.docs.map(async (artisanProjectSnapshot) => {
            const transformedProject = await getArtisanProjectWithImageUrl(this.getArtisanProjectImageUrl, transformDataToArtisanProject(artisanProjectSnapshot.data() as IDocumentDataArtisanProject));

            return new ArtisanProjectDocument(artisanProjectSnapshot.id, transformedProject);
        }));

        return artisanProjects;
    };

    /** Update artisan project meta-data
     * @param artisanProjectId - The id of the artisan project
     * @param shouldBePublished - Should the artisan project be published ?
     * @param shouldBeFavorite - Should the artisan project be favorite ?
     * @param shouldBeDeleted - Should the artisan project be deleted ?
     * @return {Promise<string>} */
    public readonly updateArtisanProjectMetaData = async (
        artisanProjectId: string,
        shouldBePublished?: boolean,
        shouldBeFavorite?: boolean,
        shouldBeDeleted?: boolean,
    ) => {
        if (artisanProjectId === undefined) throw new Error('artisanProjectId is undefined');
        if (shouldBePublished === undefined
            && shouldBeFavorite === undefined
            && shouldBeDeleted === undefined
        ) throw new Error('shouldBePublished and shouldBeFavorite are undefined');

        const result = await httpsCallable<unknown, {uid:string}>(this.getFirebaseFunctions(), 'updateArtisanProjectMetaData')({
            artisanProjectId,
            shouldBePublished,
            shouldBeFavorite,
            shouldBeDeleted,
        });

        if (!result.data?.uid) throw new Error(`updateArtisanProjectMetaData fails because of ${result.data}`);

        return result.data.uid;
    };

    /*----------------*/
    /*    REQUESTS    */
    /*----------------*/

    private readonly getRequestEstimateAttachmentPathRef = (id:string) => ref(this.getStorage(), `${FirebaseStorageBucketFolder.RequestEstimateAttachments}/${id}`);

    private getRequestEstimateAttachementData = async (id: string) => {
        const storageRef = this.getRequestEstimateAttachmentPathRef(id);
        const { contentType } = await getMetadata(storageRef);

        return {
            url: await getDownloadURL(storageRef),
            contentType,
        };
    };

    /** Uploads an request estimate files
     * @param id | file id
     * @param imageBlob | The file Blob
     * @return {Promise<{id: string, url: string}>} */
    public readonly uploadRequestEstimationImage = async (
        id: string,
        imageBlob: Blob,
    ) => {
        const storageChildRef = this.getRequestEstimateAttachmentPathRef(id);

        await uploadBytes(storageChildRef, imageBlob);

        return { id, url: await getDownloadURL(storageChildRef) };
    }

    private readonly getRequestQuoteAttachmentPathRef = (id:string) => ref(this.getStorage(), `${FirebaseStorageBucketFolder.RequestQuoteAttachments}/${id}`);

    private getRequestQuoteAttachementData = async (id: string) => {
        const storageRef = this.getRequestQuoteAttachmentPathRef(id);
        const { contentType } = await getMetadata(storageRef);

        return {
            url: await getDownloadURL(storageRef),
            contentType,
        };
    };

    /** Uploads an request quote files
     * @param id | file id
     * @param imageBlob | The file Blob
     * @return {Promise<{id: string, url: string}>} */
    public readonly uploadRequestQuote = async (
        id: string,
        imageBlob: Blob,
    ) => {
        const storageChildRef = this.getRequestQuoteAttachmentPathRef(id);

        await uploadBytes(storageChildRef, imageBlob);

        return { id, url: await getDownloadURL(storageChildRef) };
    }

    /** Uploads an request estimate files
     * @param id | file id
     * @param imageBlob | The file Blob
     * @return {Promise<{id: string, url: string}>} */
    public readonly uploadRequestQuoteImage = async (
        id: string,
        imageBlob: Blob,
    ) => {
        const storageChildRef = this.getRequestQuoteAttachmentPathRef(id);

        await uploadBytes(storageChildRef, imageBlob);

        return { id, url: await getDownloadURL(storageChildRef) };
    }

    /** Creates a new request for an artisan
     * @param submissionId - The submission id associated with the request
     * @param artisanId - The artisan id to attach the request to
     * @returns {Promise<string>} - The request reference id */
    public readonly createRequest = async (submissionId: string | undefined, artisanId: string, estimate?: IRequestEstimate) => {
        if (submissionId === undefined) throw new Error('submissionId is undefined');
        const result = await httpsCallable<unknown, {uid:string}>(this.getFirebaseFunctions(), 'createRequest')({
            submissionId,
            artisanId,
            estimate,
        });
        if (!result.data?.uid) throw new Error(`createRequest fails because of ${result.data}`);

        return result.data.uid;
    }

    /** add Message on Request
     * @param request
     * @param message
     * @returns {Promise<void>} */
    public readonly sendRequestMessage = async ({ id, data:{ fields:{ contact:{ email } } } }: RequestDocument, message: string) => {
        if (id === undefined) throw new Error('requestId is undefined');
        const result = await httpsCallable<unknown, string>(this.getFirebaseFunctions(), 'sendMessage')({
            requestId : id,
            email,
            from      : RequestMessageSource.Artisan,
            text      : message,
        });

        if (result.data !== 'OK') throw new Error(`sendRequestMessage fails because of ${result.data}`);
    }

    /** notify Request messages read
     * @param requestId
     * @returns {Promise<void>} */
    public readonly readRequestMessage = async (requestId : string) => {
        if (requestId === undefined) throw new Error('requestId is undefined');
        const result = await httpsCallable<unknown, string>(this.getFirebaseFunctions(), 'readRequest')({
            requestId,
            from: RequestMessageSource.Artisan,
        });

        if (result.data !== 'OK') throw new Error(`readRequestMessages fails because of ${result.data}`);
    }

    /** Update isArchived prop on list of requests
     * @param requestIds - The requests list id
     * @param isArchived - boolean
     * @returns {Promise<void>} */
    public readonly updateRequestsIsArchived = async (requestIds: string[], isArchived: boolean) => {
        if (requestIds.length === 0) throw new Error('requestIds is empty');

        const result = await httpsCallable(this.getFirebaseFunctions(), 'updateRequests')({
            requestIds,
            isArchived,
        });

        if (result.data !== 'OK') throw new Error(`updateRequests fails because of ${result.data}`);
    }

    /** Update isRequestsArchiveAutoEnabled prop on artisan
     * @param artisanId - The artisan id
     * @param isRequestsArchiveAutoEnabled - boolean
     * @returns {Promise<void>} */
    public readonly updateIsRequestsArchiveAutoEnabled = async (artisanId: string, isRequestsArchiveAutoEnabled: boolean) => {
        const result = await httpsCallable<unknown, {uid:string}>(this.getFirebaseFunctions(), 'updateArtisan')({
            artisanId,
            isRequestsArchiveAutoEnabled,
        });
        if (!result.data?.uid) throw new Error(`updateArtisan fails because of ${result.data}`);
    }

    /** Gets the request form the firestore database and returns it
     * @param requestId - The id of the request
     * @return {Promise<RequestDocument>} - The request */
    public readonly getRequest = async (requestId: string) => {
        const requestsSnapshots = await getDoc(doc(this.getRequestsCollectionReference(), requestId));

        return new RequestDocument(requestsSnapshots.id, transformDataToRequest(requestsSnapshots.data() as IDocumentDataRequest));
    };

    /** Gets the request form the firestore database and returns it
     * @param estimate - IRequestEstimate
     * @return {Promise<RequestDocument>} - The request */
    public readonly getEstimateWithAttachmentsUrl = async (estimate: IRequestEstimate) => getEstimateAttachmentsWithUrl(this.getRequestEstimateAttachementData, estimate);

    /** Gets the request form the firestore database and returns it
     * @param quote - IRequestQuote
     * @return {Promise<RequestDocument>} - The request */
    public readonly getQuoteWithAttachmentsUrl = async (quote: IRequestQuote) => getQuoteAttachmentsWithUrl(this.getRequestQuoteAttachementData, quote);

    /** Accepts/declines the request in the firestore database
     * @param requestId - The id of the request
     * @param status - The status of the request
     * @return {Promise<void>} */
    public readonly answerRequest = async (requestId: string, status: RequestStatusId, declineReasons?: IRequestDeclineArtisanReasons, declineUserReasons?: IRequestDeclineUserReasons, estimate?: IRequestEstimate) => {
        const result = await httpsCallable(this.getFirebaseFunctions(), 'answerRequest')({
            requestId,
            status,
            declineReasons,
            declineUserReasons,
            estimate,
        });
        if (result.data !== 'OK') throw new Error(`answerRequest fails because of ${result.data}`);
    };

    /** add quote on request
     * @param requestId - The id of the request
     * @param quote
     * @return {Promise<void>} */
    public readonly addRequestQuote = async (requestId: string, quote: IRequestQuote) => {
        const result = await httpsCallable(this.getFirebaseFunctions(), 'addRequestQuote')({
            requestId,
            quote,
        });
        if (result.data !== 'OK') throw new Error(`addRequestQuote fails because of ${result.data}`);
    };

    /** Lists the form requests of a particular artisan in the firestore database
     * @param artisanId | the id of the artisan
     * @return {Promise<RequestDocument[]>} - requests */
    public readonly listArtisanRequests = async (artisanId: string) => {
        const artisanRequestsSnapshots = await getDocs(query(this.getRequestsCollectionReference(), where('artisanId', '==', artisanId)));

        const artisanRequests = artisanRequestsSnapshots.docs.map((requestSnapshot) => new RequestDocument(requestSnapshot.id, transformDataToRequest(requestSnapshot.data() as IDocumentDataRequest)));

        return artisanRequests;
    };

    public readonly makeSubmissionsSubscription = (
        callback: (submissions: SubmissionDocument[]) => void,
        from?: Date,
        onError?: (error: Error) => void,
    ) => {
        let q = query(this.getSubmissionsCollectionReference(), orderBy('creationDate'));

        if (from) {
            const timestamp = Timestamp.fromDate(from);
            q = query(q, where('creationDate', '>', timestamp));
        }

        return onSnapshot(q, (submissionSnapshots) => {
            const submissions = submissionSnapshots.docs.map((submissionSnapshot) => new SubmissionDocument(submissionSnapshot.id, transformDataToSubmission(submissionSnapshot.data() as IDocumentDataSubmission)));
            callback(submissions);
        }, (error) => { if (onError) onError(error); });
    }

    public readonly makeSubmissionSubscription = (
        callback: (submission: SubmissionDocument) => void,
        submissionId: string,
        onError?: (error: Error) => void,
    ) => (
        onSnapshot(doc(this.getSubmissionsCollectionReference(), submissionId), (submissionSnapshot) => {
            callback(new SubmissionDocument(submissionSnapshot.id, transformDataToSubmission(submissionSnapshot.data() as IDocumentDataSubmission)));
        }, (error) => { if (onError) onError(error); })
    )

    public readonly makeSubmissionRequestsSubscription = (
        submissionId: string,
        callback: (requests: RequestDocument[]) => void,
        onError?: (error: Error) => void,
    ) => (
        onSnapshot(query(this.getRequestsCollectionReference(), where('submissionId', '==', submissionId)), (requestSnapshots) => {
            const requests = requestSnapshots.docs.map((requestSnapshot) => new RequestDocument(requestSnapshot.id, transformDataToRequest(requestSnapshot.data() as IDocumentDataRequest)));
            callback(requests);
        }, (error) => { if (onError) onError(error); })
    )

    public readonly makeArtisanProjectsSubscription = (
        artisanId: string,
        callback: (projects: ArtisanProjectDocument[]) => void,
        onError?: (error: Error) => void,
    ) => (
        onSnapshot(query(this.getArtisanProjectsCollectionReference(), where('artisanId', '==', artisanId), where('isDeleted', '==', false)), async (artisanProjectsSnapshots) => {
            const artisanProjects = await Promise.all(artisanProjectsSnapshots.docs.map(async (artisanProjectSnapshot) => {
                const transformedProject = await getArtisanProjectWithImageUrl(this.getArtisanProjectImageUrl, transformDataToArtisanProject(artisanProjectSnapshot.data() as IDocumentDataArtisanProject));

                return new ArtisanProjectDocument(artisanProjectSnapshot.id, transformedProject);
            }));

            callback(artisanProjects);
        }, (error) => { if (onError) onError(error); })
    )

    public readonly makeArtisanRequestsSubscription = (
        artisanId: string,
        callback: (requests: RequestDocument[]) => void,
        onError?: (error: Error) => void,
    ) => (
        onSnapshot(query(this.getRequestsCollectionReference(), where('artisanId', '==', artisanId)), (requestSnapshots) => {
            const requests = requestSnapshots.docs.map((requestSnapshot) => new RequestDocument(requestSnapshot.id, transformDataToRequest(requestSnapshot.data() as IDocumentDataRequest)));
            callback(requests);
        }, (error) => { if (onError) onError(error); })
    )

    public readonly makeRequestsSubscription = (
        callback: (requests: RequestDocument[]) => void,
        artisanId?: string,
        from?: Date,
        onError?: (error: Error) => void,
    ) => {
        let q = query(this.getRequestsCollectionReference(), orderBy('creationDate'));

        if (artisanId) {
            q = query(q, where('artisanId', '==', artisanId));
        }

        if (from) {
            const timestamp = Timestamp.fromDate(from);
            q = query(q, where('creationDate', '>', timestamp));
        }

        return onSnapshot(q, (requestSnapshots) => {
            const requests = requestSnapshots.docs.map((requestSnapshot) => new RequestDocument(requestSnapshot.id, transformDataToRequest(requestSnapshot.data() as IDocumentDataRequest)));
            callback(requests);
        }, (error) => { if (onError) onError(error); });
    }

    public readonly makeArtisansProjectsSubscription = (
        callback: (artisansProjects: ArtisanProjectDocument[]) => void,
        artisanId?: string,
        from?: Date,
        onError?: (error: Error) => void,
    ) => {
        let q = query(this.getArtisanProjectsCollectionReference(), orderBy('creationDate'), where('isDeleted', '==', false));

        if (artisanId) {
            q = query(q, where('artisanId', '==', artisanId));
        }

        if (from) {
            const timestamp = Timestamp.fromDate(from);
            q = query(q, where('creationDate', '>', timestamp));
        }

        return onSnapshot(q, (artisanProjects) => {
            const artisansProjects = artisanProjects.docs.map((artisanProject) => new ArtisanProjectDocument(artisanProject.id, transformDataToArtisanProject(artisanProject.data() as IDocumentDataArtisanProject)));
            callback(artisansProjects);
        }, (error) => { if (onError) onError(error); });
    }

    public readonly makeArtisansSubscription = (
        onlyEnabled:boolean,
        callback: (artisans: ArtisanDocument[]) => void,
        onError?: (error: Error) => void,
    ) => {
        let q = query(this.getArtisansCollectionReference());

        if (onlyEnabled) {
            q = query(q, where('status', '==', ArtisanStatus.Enabled));
        }

        return onSnapshot(q, (artisanSnapshots) => {
            const artisans = artisanSnapshots.docs.map((artisanSnapshot) => new ArtisanDocument(artisanSnapshot.id, transformDataToArtisan(artisanSnapshot.data() as IDocumentDataArtisan)));
            callback(artisans);
        }, (error) => { if (onError) onError(error); });
    }

    public readonly makeArtisanSubscription = (
        artisanId: string,
        callback: (artisan: ArtisanDocument) => void,
        onError?: (error: Error) => void,
    ) => (
        onSnapshot(doc(this.getArtisansCollectionReference(), artisanId), (artisanSnapshot) => {
            const artisan = new ArtisanDocument(artisanSnapshot.id, transformDataToArtisan(artisanSnapshot.data() as IDocumentDataArtisan));
            if (artisan.data.status === ArtisanStatus.Enabled) {
                callback(artisan);
            } else if (onError) onError(new Error('account disabled'));
        }, (error) => { if (onError) onError(error); })
    )

    /** Cancel submission
     * @param submissionId - The id of the submission
     * @return {Promise<void>} */
    public readonly cancelSubmission = async (submissionId: string) => {
        const result = await httpsCallable(this.getFirebaseFunctions(), 'updateSubmission')({
            submissionId,
            status: SubmissionStatusId.Canceled,
        });
        if (result.data !== 'OK') throw new Error(`cancelSubmission fails because of ${result.data}`);
    };

    public readonly closeForRequests = async (submissionId: string) => {
        const result = await httpsCallable(this.getFirebaseFunctions(), 'updateSubmission')({
            submissionId,
            status: SubmissionStatusId.ClosedForRequests,
        });
        if (result.data !== 'OK') throw new Error(`closeForRequests fails because of ${result.data}`);
    };

    /*----------------*/
    /*  NOTIFICATIONS */
    /*----------------*/

    /** Notify sales when artisan want to go PRO plan
     * @param artisanId - The artisan id
     * @param artisanPhone - The artisan phone
     * @returns {Promise<void>} */
    public readonly requestPremiumUpgrade = async (artisanId: string, artisanPhone:string) => {
        const result = await httpsCallable(this.getFirebaseFunctions(), 'requestPremiumUpgrade')({ artisanId, artisanPhone });
        if (result.data !== 'OK') throw new Error(`requestPremiumUpgrade fails because of ${result.data}`);
    }

    /*----------------*/
    /*    TRACKING    */
    /*----------------*/

    public readonly trackPage = (path:string) => {
        logEvent(this.getAnalytics(), 'page_view', {
            page_path: path,
        });
    }

    public readonly trackSetSimpleValue = (id:DataId, value:string) => {
        logEvent(this.getAnalytics(), ActionId.SET, { id, type: ValueType.SIMPLE, value });
    }

    public readonly trackSetDimension = (id:IFormFieldKey, dimension:IFormFieldDimension) => {
        logEvent(this.getAnalytics(), ActionId.SET, {
            id,
            type      : ValueType.DIMENSION,
            height    : dimension[FieldDimensionId.Height]?.value,
            width     : dimension[FieldDimensionId.Width]?.value,
            length    : dimension[FieldDimensionId.Length]?.value,
            thickness : dimension[FieldDimensionId.Thickness]?.value,
        });
    }

    public readonly trackEditValue = (id:IFormFieldKey) => {
        logEvent(this.getAnalytics(), ActionId.EDIT, { id });
    }

    public readonly trackClick = (id:CtaId, from:FromId) => {
        logEvent(this.getAnalytics(), ActionId.CLICK, { id, from });
    }

    public readonly trackAction = (action:ActionId, extra?:{ [key: string]: any }) => {
        logEvent(this.getAnalytics(), action, extra);
    }

    public readonly setUserProperty = (property:UserProperty, value:string) => {
        setUserProperties(this.getAnalytics(), { [property]: value });
    }

    private readonly getUniqueFileName = (file: File): string => `${uuid()}.${file.name.split('.').pop()}`
}

export default FirebaseService;
